import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface EppoSpecies {
  code: string;
  latinName: string;
  englishName?: string;
}

const EPPO_SERVICE = 'https://eppo-service.supplycanvas.com/api/eppo';

/**
 * Code → name lookup against the external EPPO microservice.
 *
 * Sites store species as bare EPPO codes (`TEUGR`), so every read-only surface that wants to show
 * a name has to resolve them. A chain repeats the same handful of codes across many sites, hence
 * the process-wide cache: it holds the in-flight *promise*, so simultaneous badges on one screen
 * collapse into a single request instead of racing.
 *
 * The service is external and only decorative for reading — a failure degrades to the bare code
 * and is dropped from the cache, so the next attempt retries instead of pinning the outage for
 * the rest of the session.
 */
@Injectable({ providedIn: 'root' })
export class EppoSpeciesService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<string, Promise<EppoSpecies>>();

  resolve(code: string): Promise<EppoSpecies> {
    const cached = this.cache.get(code);
    if (cached) return cached;

    const pending = firstValueFrom(this.http.get<EppoSpecies>(`${EPPO_SERVICE}/species/${encodeURIComponent(code)}`))
      .then((species) => species ?? this.unresolved(code))
      .catch(() => {
        this.cache.delete(code);
        return this.unresolved(code);
      });

    this.cache.set(code, pending);
    return pending;
  }

  resolveAll(codes: string[]): Promise<EppoSpecies[]> {
    return Promise.all([...new Set(codes)].map((code) => this.resolve(code)));
  }

  async search(term: string): Promise<EppoSpecies[]> {
    try {
      const results = await firstValueFrom(
        this.http.get<EppoSpecies[]>(`${EPPO_SERVICE}/search?q=${encodeURIComponent(term)}`)
      );
      return Array.isArray(results) ? results : [];
    } catch {
      return [];
    }
  }

  /** `Tectona grandis (TEUGR)` — or the bare code when the name could not be resolved. */
  label(species: EppoSpecies): string {
    return species.latinName && species.latinName !== species.code
      ? `${species.latinName} (${species.code})`
      : species.code;
  }

  private unresolved = (code: string): EppoSpecies => ({ code, latinName: code });
}
